/*
 * The last line of defence: a render error shows a message instead of a white page.
 *
 * ☠️ **Written because one missing field blanked the entire site.** The party page was served
 * by an API process predating its `speakers` field, `data.speakers.map(...)` threw, and React
 * unmounted the whole tree — no header, no footer, no text, nothing a visitor could act on. The
 * stale process was the cause and was restarted, but the *severity* was the real defect: a
 * public site must not have a failure mode where a single component takes the page to blank.
 *
 * ⚠️ This is not a licence to render junk. It deliberately does **not** paper over missing data
 * with defaults — a page quietly showing an empty list because the API changed shape is worse
 * than one saying it is broken, since nobody finds out. The boundary makes a crash *legible and
 * contained*; the fix still belongs at the source.
 *
 * It matters most at the Phase 2b cutover, where a deployed frontend meets whatever API version
 * is actually on the server — precisely the mismatch that caused this.
 *
 * A class, because `componentDidCatch` has no hook equivalent.
 */

import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

type Props = { children: ReactNode };
type State = { error: Error | null };

export class ErrorBoundary extends Component<Props, State> {
    state: State = { error: null };

    static getDerivedStateFromError(error: Error): State {
        return { error };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        // Reported, never swallowed — the same rule the page containers follow for fetches.
        console.error('Unbehandelter Fehler beim Rendern', error, info.componentStack);
    }

    render() {
        if (!this.state.error) return this.props.children;

        return (
            <main className="dq-page" id="inhalt">
                <div className="dq-errorboundary" role="alert">
                    <h1>Diese Seite konnte nicht angezeigt werden</h1>
                    <p>
                        Beim Aufbau der Seite ist ein Fehler aufgetreten. Das liegt an dieser
                        Website, nicht an Ihrem Gerät.
                    </p>
                    <p>
                        <a href="/">Zur Startseite</a>
                    </p>
                    {/* The message, not a stack: enough for a reader to report it usefully,
                        and it is already in the console in full for anyone debugging. */}
                    <p className="dq-meta">{this.state.error.message}</p>
                </div>
            </main>
        );
    }
}
